import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw, Brain, AlertTriangle, Info } from 'lucide-react';
import { AIInsightData } from './types';
import { formatDistanceToNow } from 'date-fns';

interface AIInsightCardProps {
  insight: AIInsightData | null;
  loading: boolean;
  onRefreshInsight: () => void;
}

// Simple markdown formatter for basic bold text
const formatMarkdownText = (text: string): React.ReactNode => {
  if (!text) return text;
  
  // Split text by **bold** patterns
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    // Check if this part is bold (wrapped in **)
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2); // Remove ** from start and end
      return <strong key={index} className="font-semibold">{boldText}</strong>;
    }
    return part;
  });
};

export const AIInsightCard: React.FC<AIInsightCardProps> = ({
  insight,
  loading,
  onRefreshInsight,
}) => {

  const renderLoadingState = () => (
     <Card className="h-full"> {/* Ensure card takes full height */}
      <CardHeader className="flex flex-row items-center justify-between pb-2">
         <div className="flex items-center gap-2">
           <Skeleton className="h-5 w-5 rounded-full" />
           <Skeleton className="h-6 w-2/5" />
         </div>
         <Skeleton className="h-8 w-8 rounded-full" />
      </CardHeader>
      <CardContent className="pt-2 space-y-4 h-[calc(100%-60px)]"> {/* Adjust height */}
        <div className="flex items-center gap-2 animate-pulse">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-3 rounded-full" />
          <Skeleton className="h-3 w-20" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2 mt-2" />
        </div>
      </CardContent>
    </Card>
  );

 const renderEmptyState = () => (
    <Card className="h-full"> {/* Ensure card takes full height */}
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" /> Stewie's Insights
        </CardTitle>
         <Button variant="ghost" size="icon" onClick={onRefreshInsight} className="h-8 w-8">
            <RefreshCw className="h-4 w-4" />
         </Button>
      </CardHeader>
      <CardContent className="h-[calc(100%-60px)] flex flex-col items-center justify-center"> {/* Adjust height and center content */}
        <div className="text-center">
             <Brain className="h-10 w-10 text-gray-300 mb-3 mx-auto" />
             <h3 className="text-base font-medium text-gray-500">Stewie is Analyzing...</h3>
             <p className="text-xs text-gray-400 max-w-xs mt-1">
               Actionable insights based on your portfolio and market conditions will appear here shortly.
             </p>
        </div>
      </CardContent>
    </Card>
 );

  if (loading) {
    return renderLoadingState();
  }

  if (!insight) {
    return renderEmptyState();
  }

  const { timestamp, category, text, severity, model_used } = insight;

  const getSeverityStyles = () => {
    switch (severity) {
      case 'critical':
        return { icon: <AlertTriangle className="w-4 h-4 text-red-500" />, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
      case 'warning':
        return { icon: <AlertTriangle className="w-4 h-4 text-amber-500" />, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
      default: // info or undefined
        return { icon: <Info className="w-4 h-4 text-blue-500" />, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' };
    }
  };

  const styles = getSeverityStyles();

  return (
    <Card className={`h-full border ${styles.border} ${styles.bg}`}> {/* Ensure card takes full height */}
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className={`text-lg font-medium flex items-center gap-2 ${styles.color}`}>
          <Sparkles className="w-5 h-5" /> Stewie's Insights
        </CardTitle>
         <Button variant="ghost" size="icon" onClick={onRefreshInsight} className={`h-8 w-8 ${styles.color} hover:${styles.bg.replace('50','100')}`}>
            <RefreshCw className="h-4 w-4" />
         </Button>
      </CardHeader>
      <CardContent className="pt-2 h-[calc(100%-60px)] overflow-y-auto"> {/* Adjust height and add scroll */}
         <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
            {styles.icon}
            <span className="font-medium">{category} Insight</span>
            <span>·</span>
            <span title={new Date(timestamp).toLocaleString()}>
                {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
            </span>
            {model_used && (
              <>
                <span>·</span>
                <span className="italic" title="AI model used for this insight">
                  {model_used === 'stewie_quant_llama4' ? 'Llama 4' : 
                   model_used === 'fallback_model' ? 'Fallback' : 
                   model_used}
                </span>
              </>
            )}
         </div>
        <p className={`text-sm ${styles.color}`}>
            {formatMarkdownText(text)}
        </p>
      </CardContent>
    </Card>
  );
};
